import GUI from 'lil-gui'
import * as THREE from 'three'
import {OrbitControls} from 'three/examples/jsm/controls/OrbitControls.js'
import {GLTFLoader} from 'three/examples/jsm/loaders/GLTFLoader.js'
import {DRACOLoader} from 'three/examples/jsm/loaders/DRACOLoader.js'
import {Timer} from 'three'

import {EffectComposer} from "three/examples/jsm/postprocessing/EffectComposer.js";
import {RenderPass} from "three/examples/jsm/postprocessing/RenderPass.js";
// shadow
import {CSM} from "three/addons/csm/CSM.js";
import {CSMHelper} from 'three/addons/csm/CSMHelper.js';
import {ShaderPass} from "three/examples/jsm/postprocessing/ShaderPass.js";
import {GammaCorrectionShader} from "three/examples/jsm/shaders/GammaCorrectionShader.js";

// third party
// import {BloomEffect, SSAOEffect, ToneMappingEffect, EffectComposer, EffectPass, RenderPass} from "postprocessing";

import "./style.css";
import {Mesh} from "three";
import {SMAAPass} from "three/examples/jsm/postprocessing/SMAAPass.js";
import {UnrealBloomPass} from "three/examples/jsm/postprocessing/UnrealBloomPass.js";
import {loadPrimaryTextures} from "./utils.ts";
import injectShaderSplattingMap from "./shader/injectShader.ts";
import {Sky} from "three/examples/jsm/objects/Sky.js";

// Debug
const debuggerUI = new GUI({
    width: 400
})

const windowSize = {
    width: window.innerWidth,
    height: window.innerHeight
}

// Canvas
const mainCanvas = document.querySelector('.main-render')

// Scene
const mainScene = new THREE.Scene()
// mainScene.background = new THREE.Color("#fff");

// Texture loader
const textureLoader = new THREE.TextureLoader()

// Draco loader
const dracoLoader = new DRACOLoader()
dracoLoader.setDecoderPath('draco/')

// GLTF loader
const gltfLoader = new GLTFLoader()
gltfLoader.setDRACOLoader(dracoLoader);

(() => {
    
    SetupSceneAndEnvMap();
    
    setupSky();
    
    const perspectiveCamera = SetUpCamera();
    
    const {directionalLight, csm, csmHelper} = SetupCsmLighting(perspectiveCamera);
    // const directionalLight = setupLighint();
    
    LoadModels(directionalLight, csm);
    
    const webGLRenderer = SetUpRenderer();
    
    UpdateAllMatWithShadow();
    
    const effectComposer = SetupPostProc(webGLRenderer, perspectiveCamera);
    
    // Add resizing event
    window.addEventListener('resize', () => {
        // Update sizes
        windowSize.width = window.innerWidth
        windowSize.height = window.innerHeight
        
        // Update camera
        perspectiveCamera.aspect = windowSize.width / windowSize.height
        perspectiveCamera.updateProjectionMatrix()
        
        // Update renderer
        webGLRenderer.setSize(windowSize.width, windowSize.height)
        webGLRenderer.setPixelRatio(Math.min(window.devicePixelRatio, 2))
        //effect composer
        effectComposer.setSize(windowSize.width, windowSize.height)
        effectComposer.setPixelRatio(Math.min(window.devicePixelRatio, 2))
    });
    
    //Main Tick
    const MainTimer = new Timer();
    let tick = () => {
        MainTimer.update();
        // const elapsedTime = MainTimer.getElapsed();
        
        csm.update();
        csmHelper.update();
        
        // Render
        webGLRenderer.render(mainScene, perspectiveCamera)
        // effectComposer.render();
        
        
        requestAnimationFrame(tick)
    }
    tick();
})();


function SetupSceneAndEnvMap() {
    // load cube env maps
    const cubeTextureLoader = new THREE.CubeTextureLoader();
    const cubeTexture = cubeTextureLoader.setPath('cubeEnvMaps/').load([
        'px.png',
        'nx.png',
        'py.png',
        'ny.png',
        'pz.png',
        'nz.png'
    ]);
    
    // const rgbeLoader = new RGBELoader()
    // rgbeLoader.load('hdri/aristea_wreck_1k.hdr', (environmentMap: any) => {
    //     environmentMap.mapping = THREE.EquirectangularReflectionMapping
    //
    //     mainScene.background = environmentMap
    //     mainScene.environment = environmentMap
    // })
    
    // set env map to light the scene
    // mainScene.background = cubeTexture;
    mainScene.backgroundBlurriness = .1;
    mainScene.environment = cubeTexture;
    
    
    mainScene.environmentIntensity = 2;
    
    //HDRI
// const HDRITexture = new RGBELoader().setPath("hdri/").load('aristea_wreck_1k.hdr', (envMap) => {
//     envMap.mapping = THREE.EquirectangularReflectionMapping;
//     mainScene.background = envMap;
//     mainScene.environment = envMap;
//     mainScene.backgroundBlurriness = .3
//
//     // const groundedSkybox = new GroundedSkybox(envMap, 15, 500);
//     // // groundedSkybox.material.wireframe = true;
//     // groundedSkybox.position.y = -30;
//     // mainScene.add(groundedSkybox);
// });
    debuggerUI.add(mainScene, "environmentIntensity").min(0).max(10).step(0.01).name("env map intensity");
    debuggerUI.add(mainScene, "backgroundBlurriness").min(0).max(1).step(0.1).name("backgroundBlurriness");
}

function setupSky() {
    const sky = new Sky();
    sky.scale.setScalar(10000);
    mainScene.add(sky);
    
    const skyUniforms = sky.material.uniforms
    skyUniforms['turbidity'].value = .5;
    skyUniforms['rayleigh'].value = .15;
    skyUniforms['mieCoefficient'].value = .005;
    skyUniforms['mieDirectionalG'].value = 0.7;
    skyUniforms['sunPosition'].value.set(.1, 1, 1)
    
    const skyfolder = debuggerUI.addFolder("sky");
    skyfolder.add(skyUniforms['sunPosition'].value, "x").min(-1).max(1).step(0.01).name("sunPosition.x");
    skyfolder.add(skyUniforms['sunPosition'].value, "y").min(-1).max(1).step(0.01).name("sunPosition.y");
    skyfolder.add(skyUniforms['sunPosition'].value, "z").min(-1).max(1).step(0.01).name("sunPosition.z");
    skyfolder.add(skyUniforms['turbidity'], "value").min(0).max(20).step(0.01).name("turbidity");
    skyfolder.add(skyUniforms['rayleigh'], "value").min(0).max(4).step(0.01).name("rayleigh");
    skyfolder.add(skyUniforms['mieCoefficient'], "value").min(0).max(1).step(0.01).name("mieCoefficient");
    skyfolder.add(skyUniforms['mieDirectionalG'], "value").min(0).max(1).step(0.01).name("mieDirectionalG");
    
    
}

function SetupCsmLighting(camera: THREE.PerspectiveCamera) {
    
    // uniformed params to for csm and lighting also in gui
    let LightingPos = {
        x: -1,
        y: -1,
        z: -1
    }
    
    const ambientLight = new THREE.AmbientLight("#fff", 1)
    mainScene.add(ambientLight);
    // new THREE.CameraHelper(ambientLight.)
    
    // const directionalLight = new THREE.DirectionalLight(0x000020, 1.5);
    const directionalLight = new THREE.DirectionalLight("#fff", 1.5);
    // turn into positive number, csm lighting and this "fake lighting" need to "point to" one another
    const FakeLightScale = -200;
    directionalLight.position.set(LightingPos.x, LightingPos.y, LightingPos.z).normalize().multiplyScalar(FakeLightScale);
    
    const directionalLightHelper = new THREE.DirectionalLightHelper(directionalLight);
    
    mainScene.add(directionalLight);
    mainScene.add(directionalLightHelper);
    
    let csm = new CSM({
        lightDirection: new THREE.Vector3(LightingPos.x, LightingPos.y, LightingPos.z).normalize(),
        cascades: 2,
        parent: mainScene,
        camera: camera,
        shadowMapSize: 1024,
        lightFar: 500,
        maxFar: 500,
        mode: "practical",
    });
    let csmHelper = new CSMHelper(csm);
    csmHelper.visible = false;
    mainScene.add(csmHelper);
    csmHelper.updateVisibility();
    
    const lightingFolder = debuggerUI.addFolder('Lighting');
    lightingFolder.add(LightingPos, 'x', -1, 1).name('light x').onChange(function (value: number) {
        csm.lightDirection.x = value;
        directionalLight.position.x = value * FakeLightScale;
        
    });
    
    lightingFolder.add(LightingPos, 'y', -1, 1).name('light y').onChange(function (value: number) {
        csm.lightDirection.y = value;
        directionalLight.position.y = value * FakeLightScale;
    });
    
    lightingFolder.add(LightingPos, 'z', -1, 1).name('light z').onChange(function (value: number) {
        csm.lightDirection.z = value;
        directionalLight.position.z = value * FakeLightScale;
        
    });
    
    lightingFolder.open();
    
    const helperFolder = debuggerUI.addFolder('CSM Helper');
    helperFolder.add(csmHelper, 'visible');
    
    helperFolder.add(csmHelper, 'displayFrustum').onChange(function () {
        
        csmHelper.updateVisibility();
        
    });
    
    helperFolder.add(csmHelper, 'displayPlanes').onChange(function () {
        
        csmHelper.updateVisibility();
        
    });
    
    helperFolder.add(csmHelper, 'displayShadowBounds').onChange(function () {
        
        csmHelper.updateVisibility();
        
    });
    
    helperFolder.open();
    
    
    debuggerUI.add(directionalLight, "intensity", 0, 10).step(1).name("light intensity");
    // debuggerUI.add(ambientLight, "intensity", 0, 10).step(1).name("ambient intensity");
    
    return {directionalLight, csm, csmHelper};
}

// function setupLighint() {
//     // uniformed params to for csm and lighting also in gui
//     const LightingPos = {
//         x: -1,
//         y: -1,
//         z: -1
//     }
//
//     const ambientLight = new THREE.AmbientLight("#fff", .5)
//     mainScene.add(ambientLight);
//
//     // const directionalLight = new THREE.DirectionalLight(0x000020, 1.5);
//     const directionalLight = new THREE.DirectionalLight("#fff", 2.5);
//     // turn into positive number, csm lighting and this "fake lighting" need to "point to" one another
//     const camerScaleFac = -100;
//     directionalLight.position.set(LightingPos.x, LightingPos.y, LightingPos.z).normalize().multiplyScalar(camerScaleFac);
//
//     const directionalLightHelper = new THREE.DirectionalLightHelper(directionalLight);
//     directionalLight.castShadow = true;
//     directionalLight.shadow.camera.left = -100;
//     directionalLight.shadow.camera.right = 100;
//     directionalLight.shadow.camera.top = 100;
//     directionalLight.shadow.camera.bottom = -100;
//     directionalLight.shadow.camera.near = 1;
//     directionalLight.shadow.camera.far = 300;
//     directionalLight.shadow.mapSize.set(1024, 1024);
//
//     const directionalLightCameraHelper = new THREE.CameraHelper(directionalLight.shadow.camera) //shadow camera
//     mainScene.add(directionalLight);
//     mainScene.add(directionalLightHelper);
//     mainScene.add(directionalLightCameraHelper);
//
//
//     const lightingFolder = debuggerUI.addFolder('Lighting');
//     lightingFolder.add(LightingPos, 'x', -1, 1).name('light x').onChange(function (value: number) {
//         directionalLight.position.x = value * camerScaleFac;
//
//     });
//
//     lightingFolder.add(LightingPos, 'y', -1, 1).name('light y').onChange(function (value: number) {
//         directionalLight.position.y = value * camerScaleFac;
//     });
//
//     lightingFolder.add(LightingPos, 'z', -1, 1).name('light z').onChange(function (value: number) {
//         directionalLight.position.z = value * camerScaleFac;
//
//     });
//
//     return directionalLight;
// }

async function LoadModels(lighting?: THREE.DirectionalLight, csm?: CSM) {
    // load house
    gltfLoader.load("Sci-Fi Set_Output_built_EXP_PBRAO.glb", (gltf) => {
        const houseRigScene = gltf.scene;
        houseRigScene.traverse(child => {
            if (child.type === "Mesh") {
                console.log();
                child.castShadow = true;
                child.receiveShadow = true;
                const material = (child as Mesh).material;
                if (csm && !Array.isArray(material)) {
                    csm.setupMaterial(material);
                }
            }
        })
        houseRigScene.position.set(43, 1.6, -115)
        mainScene.add(houseRigScene);
        if (lighting && houseRigScene) {
            lighting.target.position.set(houseRigScene.position.x, houseRigScene.position.y, houseRigScene.position.z);
            lighting.target.updateWorldMatrix(true, true);
        }
    });
    
    
    const {baseColorTexture, aoMapTexture, normalMapTexture,} = loadPrimaryTextures(textureLoader);
    
    // terrain fading
    const landscapeAlphaMap = textureLoader.load("Landscape_Terrain_PBR_Alpha.png");
    landscapeAlphaMap.flipY = false;
    
    const terrainMat = new THREE.MeshStandardMaterial({
        map: baseColorTexture,
        normalMap: normalMapTexture,
        normalMapType: THREE.TangentSpaceNormalMap,
        aoMap: aoMapTexture,
        alphaMap: landscapeAlphaMap,
        transparent: true,
    });
    
    // Load terrain
    gltfLoader.load("Terrain-bare.glb", (Landscape) => {
        
        Landscape.scene.traverse((child) => {
            if (child.type === "Mesh") {
                child.castShadow = true;
                child.receiveShadow = true;
                (child as Mesh).material = terrainMat;
                let material = (child as Mesh).material;
                if (!Array.isArray(material)) {
                    if (csm)
                        csm.setupMaterial(material); //fixme: csm for terrain may not be working at all
                    injectShaderSplattingMap(material, textureLoader);
                }
            }
        });
        Landscape.scene.position.set(0, 0, 0)
        mainScene.add(Landscape.scene);
    });
}

function SetUpCamera() {
    
    // Base camera
    const camera = new THREE.PerspectiveCamera(45, windowSize.width / windowSize.height, 1, 1000);
    // camera.position.x = 4
// camera.position.y = 10
// camera.position.z = 4
    camera.position.set(50, 30, -205)
    mainScene.add(camera)
    
    // Controls
    const controls = new OrbitControls(camera, (mainCanvas as HTMLCanvasElement))
    controls.enableDamping = true
    // controls.maxPolarAngle = Math.PI / 2;
    
    let tick = () => {
        {
            camera.updateMatrixWorld();
            // Update controls
            controls.update()
            
            // Call tick again on the next frame
            requestAnimationFrame(tick)
        }
    }
    tick();
    
    return camera;
}

function UpdateAllMatWithShadow() {
    
    // mainScene.traverse((child) => {
    //     if (child.type === "Scene") {
    //         child.traverse((grandChild) => {
    //             console.log(grandChild);
    //             if (grandChild.type === "Mesh") {
    //                 const mesh = grandChild as Mesh
    //                 mesh.castShadow = true;
    //                 mesh.receiveShadow = true;
    //             }
    //         })
    //     }
    //     if (child.type === "Mesh") {
    //         const mesh = child as Mesh
    //         mesh.castShadow = true;
    //         mesh.receiveShadow = true;
    //     }
    // })
}

function SetUpRenderer() {
    /**
     * Renderer
     */
    const renderer = new THREE.WebGLRenderer({
        canvas: (mainCanvas as HTMLCanvasElement),
        antialias: true
    })
    renderer.setSize(window.innerWidth, window.innerHeight);
    renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));
    // shadow
    renderer.shadowMap.enabled = true;
    renderer.shadowMap.type = THREE.PCFSoftShadowMap;
    
    renderer.toneMapping = THREE.LinearToneMapping;
    debuggerUI.add(renderer, "toneMapping", {
        "No Tone Mapping": THREE.NoToneMapping,
        "Linear Tone Mapping": THREE.LinearToneMapping,
        "Reinhard Tone Mapping": THREE.ReinhardToneMapping,
        "Cineon Tone Mapping": THREE.CineonToneMapping,
        "ACES Filmic Tone Mapping": THREE.ACESFilmicToneMapping,
        "UVMapping": THREE.UVMapping,
    }).name("toneMapping");
    debuggerUI.add(renderer, "toneMappingExposure", 0, 10).step(0.01).name("toneMappingExposure");
    renderer.outputColorSpace = THREE.SRGBColorSpace;
    
    return renderer;
    
}

function SetupPostProc(renderer: THREE.WebGLRenderer, camera: THREE.PerspectiveCamera) {
    
    // create own render target
    const renderTarget = new THREE.WebGLRenderTarget(window.innerWidth, window.innerHeight, {
        samples: renderer.getPixelRatio() === 1 ? 2 : 0, // conditional AA
    });
    
    const effectComposer = new EffectComposer(renderer, renderTarget);
    // effectComposer.
    effectComposer.setPixelRatio(Math.min(window.devicePixelRatio, 2));
    effectComposer.setSize(window.innerWidth, window.innerHeight);
    
    const renderPass = new RenderPass(mainScene, camera);
    effectComposer.addPass(renderPass);
    
    // const ssrPass = new SSRPass(mainScene, camera);
    // effectComposer.addPass(ssrPass);
    
    // const rgbShaderPass = new ShaderPass(RGBShiftShader);
    // effectComposer.addPass(rgbShaderPass);
    
    // const ssao = new SSAOPass(mainScene, camera);
    // effectComposer.addPass(ssao);
    const resolution = new THREE.Vector2(window.innerWidth, window.innerHeight);
    const unrealBloomPass = new UnrealBloomPass(resolution, .2, .3, .7);
    // unrealBloomPass.strength = .2;
    // unrealBloomPass.radius = .3;
    // unrealBloomPass.threshold = .7;
    effectComposer.addPass(unrealBloomPass);
    
    debuggerUI.add(unrealBloomPass, "enabled")
    debuggerUI.add(unrealBloomPass, "strength", 0, 1).step(0.1).name("bloom strength");
    debuggerUI.add(unrealBloomPass, "radius", 0, 2).step(0.1).name("bloom radius");
    debuggerUI.add(unrealBloomPass, "threshold", 0, 1).step(0.1).name("bloom threshold");
    
    //SMAA pass
    const smaaPass = new SMAAPass();
    if (renderer.getPixelRatio() === 1 && !renderer.capabilities.isWebGL2) {
        // no render target supported, use postprocess AA
        console.log("SMAA Pass enabled");
        effectComposer.addPass(smaaPass);
    }
    
    const gammaShaderPass = new ShaderPass(GammaCorrectionShader);
    effectComposer.addPass(gammaShaderPass);
    
    return effectComposer;
}




